import React, { useEffect, useMemo, useState } from "react";
import { addDays, format, isBefore, subMonths } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { getDateFnsLocale } from "../../utils/getDateFnsLocale";

const TWO_MONTH_MEDIA_QUERY = "(min-width: 640px)";

export const useResponsiveMonthsShown = (
  mediaQuery = TWO_MONTH_MEDIA_QUERY,
) => {
  const [monthsShown, setMonthsShown] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? Number(window.matchMedia(mediaQuery).matches) + 1
      : 1,
  );

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return undefined;

    const query = window.matchMedia(mediaQuery);
    const sync = () => setMonthsShown(query.matches ? 2 : 1);

    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [mediaQuery]);

  return monthsShown;
};

const earliest = (...dates) => {
  const valid = dates.filter(Boolean);
  if (!valid.length) return undefined;
  return valid.reduce((min, date) => (isBefore(date, min) ? date : min));
};

/**
 * Fully controlled: pass `startDate`/`endDate` and handle `onChange`, which
 * fires with `{ startDate, endDate }` (either may be null while the user is
 * mid-selection).
 *
 * @param {Date|null}  startDate     Selected range start.
 * @param {Date|null}  endDate       Selected range end.
 * @param {Function}   onChange      ({ startDate, endDate }) => void
 * @param {Date}       [minDate]     Earliest selectable day.
 * @param {Date}       [maxDate]     Latest selectable day.
 * @param {number}     [maxRangeDays] Caps how far the end may sit past the
 *                                    start, applied while the end is pending.
 * @param {number}     [monthsShown] Overrides the responsive panel count.
 * @param {Date}       [openToDate]  Overrides which month opens first.
 * @param {boolean}    [disabled]    Blocks interaction and dims the calendar.
 */
const DateRangeCalendar = ({
  startDate = null,
  endDate = null,
  onChange,
  minDate,
  maxDate,
  maxRangeDays,
  monthsShown: monthsShownProp,
  openToDate: openToDateProp,
  disabled = false,
  className = "",
  testId = "date-range-calendar",
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = useMemo(
    () => getDateFnsLocale(i18n.language),
    [i18n.language],
  );

  const responsiveMonthsShown = useResponsiveMonthsShown();
  const monthsShown = monthsShownProp ?? responsiveMonthsShown;

  const effectiveMaxDate = useMemo(() => {
    if (!maxRangeDays || !startDate || endDate) return maxDate;
    return earliest(addDays(startDate, maxRangeDays - 1), maxDate);
  }, [maxRangeDays, startDate, endDate, maxDate]);

  const hasCustomOpenToDate = !!openToDateProp;
  const openToAnchor = openToDateProp ?? maxDate;
  const openToAnchorTime = openToAnchor?.getTime();
  const openToDate = useMemo(() => {
    const anchor =
      openToAnchorTime === undefined ? new Date() : new Date(openToAnchorTime);
    if (hasCustomOpenToDate) return anchor;
    return monthsShown > 1 ? subMonths(anchor, monthsShown - 1) : anchor;
  }, [openToAnchorTime, monthsShown, hasCustomOpenToDate]);

  const handleChange = (range) => {
    const [nextStart = null, nextEnd = null] = range || [];
    onChange?.({ startDate: nextStart, endDate: nextEnd });
  };

  return (
    <div
      id={testId}
      className={`cf-date-range-calendar ${
        disabled ? "pointer-events-none opacity-60" : ""
      } ${className}`}
      aria-disabled={disabled || undefined}
    >
      <DatePicker
        inline
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={handleChange}
        minDate={minDate}
        maxDate={effectiveMaxDate}
        openToDate={openToDate}
        monthsShown={monthsShown}
        disabledKeyboardNavigation={disabled}
        locale={dateLocale}
        calendarStartDay={0}
        formatWeekDay={(day) => day.slice(0, 3)}
        renderCustomHeader={({
          monthDate,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
          customHeaderCount,
        }) => (
          <div className="mb-2 flex items-center justify-between px-1">
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              aria-label={t("workspace:previous_month")}
              className={`rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent ${
                customHeaderCount === 0 ? "" : "invisible"
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold capitalize text-gray-900">
              {format(monthDate, "MMMM yyyy", { locale: dateLocale })}
            </span>
            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              aria-label={t("workspace:next_month")}
              className={`rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent ${
                customHeaderCount === monthsShown - 1 ? "" : "invisible"
              }`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      />
    </div>
  );
};

export default DateRangeCalendar;
